import React, { createContext, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import * as yup from 'yup';
import { isValidPhoneNumber } from 'react-phone-number-input';
import { useQuery, useMutation } from '@apollo/react-hooks';
import { GET_OPT_IN_FORM } from '../../../network/queries';
import { SUBMIT_OPT_IN_FORM } from '../../../network/mutations';

export const Context = createContext({});

const schema = yup.object().shape({
  firstName: yup.string().required('Oops! First Name can`t be blank.'),
  lastName: yup.string().required('Oops! Last Name can`t be blank.'),
  zipCode: yup
    .string()
    .matches(/^\d{5}$|^\d{5}-\d{4}$/, {
      message: 'Please enter correct value',
    })
    .required('Oops! Zip/Postal can`t be blank.'),
  questionId: yup.string().required('Please select an option.'),
  primaryPhoneNumber: yup.mixed().test({
    name: 'primaryPhoneNumber',
    message: 'Phone number is not valid',
    test: (value) => isValidPhoneNumber(value),
  }),
});

export const Provider = ({ vpn, children }) => {
  const { loading: queryLoading, error: queryError, data: queryData } = useQuery(GET_OPT_IN_FORM, {
    variables: { vpn },
  });

  const [
    createMemberViaOptInForm,
    { loading: mutationLoading, error: mutationError },
  ] = useMutation(SUBMIT_OPT_IN_FORM);

  const [fields, setFields] = useState({
    firstName: '',
    lastName: '',
    zipCode: '',
    questionId: '',
    primaryPhoneNumber: '',
  });
  const [validationError, setValidationError] = useState([]);
  const [usedFields, setUsedFields] = useState([]);

  // ====================================
  // Validation

  const handleFormValidation = (formFields) => {
    schema.validate(formFields, { abortEarly: false }).then(
      () => {
        setValidationError([]);
      },
      (response) => {
        setValidationError(response.inner);
      },
    );
  };

  useEffect(() => {
    handleFormValidation({ ...fields });
  }, [fields]);

  const updateUsedFields = (name) => {
    if (!usedFields.includes(name)) {
      usedFields.push(name);
      setUsedFields(usedFields);
      handleFormValidation(fields);
    }
  };

  const hasErrorMessage = (name) => {
    const messages = [];
    validationError.forEach((e) => {
      if (e.path === name && usedFields.includes(name)) {
        messages.push(e.message);
      }
    });
    return messages;
  };

  // ====================================
  // General Actions

  const handleUpdateValue = (name, value) => {
    setFields({ ...fields, [name]: value });
  };

  const handleUpdateFields = ({ onSuccess, onError }) => {
    const {
      firstName, lastName, zipCode, primaryPhoneNumber, questionId,
    } = fields;

    const inputData = {
      questionId,
      firstName,
      lastName,
      zipCode,
      primaryPhoneNumber,
    };

    createMemberViaOptInForm({ variables: { input: inputData } }).then(
      (result) => {
        if (typeof onSuccess === 'function') {
          onSuccess(inputData, result);
        } else {
          console.log(result);
        }
      },
      (error) => {
        if (typeof onError === 'function') {
          onError(error);
        } else {
          console.log(error);
        }
      },
    );
  };

  const contextData = {
    requestData: {
      queryLoading,
      queryError,
      queryData,
      mutationLoading,
      mutationError,
    },
    formData: fields,
    setField: handleUpdateValue,
    setFormData: setFields,
    saveData: handleUpdateFields,
    isFormValid: validationError.length === 0,
    updateUsedFields,
    hasErrorMessage,
  };

  return <Context.Provider value={contextData}>{children}</Context.Provider>;
};

export const { Consumer } = Context;

Provider.propTypes = {
  vpn: PropTypes.string.isRequired,
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]).isRequired,
};
